// ============================================================
// AlertesGamme — les trois fautes qui NE SE VOIENT PAS.
// ------------------------------------------------------------
// Elles ont une chose en commun, et c'est pour ça qu'elles vivent
// ensemble : **aucune ne produit une erreur.** Le temps est plausible,
// l'unité est valide, la base accepte, et le total est faux. Un
// deviseur ne peut pas s'en défendre parce qu'il n'y a rien à
// remarquer — §11, treizième ligne.
//
// Chacune SIGNALE et ne corrige rien. Retirer une ligne ou en ajouter
// une changerait le prix sans que personne l'ait décidé ; l'invariant
// du projet est que le programme propose et que l'homme tranche.
//
// Extrait de `GammeBloc.jsx` le 7 septembre 2026, quand la troisième
// est arrivée : trois blocs de la même forme copiés à la main, c'est
// le moment de les écrire une fois.
// ============================================================

import Badge from '../../ui/Badge.jsx';
import { formatNumber } from '../../lib/format.js';
import { effetsAppliques } from './effetsGamme.js';

/** « L'OP 20 porte » ou « Les OP 20, 30 portent ». */
const sujet = (ops, verbe, verbePluriel) => (ops.length > 1
  ? `Les OP ${ops.join(', ')} ${verbePluriel}`
  : `L’OP ${ops[0]} ${verbe}`);

const ALERTES = [
  {
    cle: 'ops_sans_montage',
    etiquette: 'montage manquant',
    verbe: ['produit', 'produisent'],
    // Le montage MANQUE : on ne l'ajoute pas, faute de savoir combien il
    // vaut. Un montage à 0 min serait un zéro converti, qui baisse le
    // prix sans que rien ne le dise (invariant 5).
    texte: 'sans ligne « mise en route » : la pièce y est usinée sans avoir été montée. '
      + 'Ajoutez le montage, ou descendez cette opération sous une OP qui en porte un. '
      + 'Les temps affichés sont justes — il en manque un.',
  },
  {
    cle: 'debits_en_prototype',
    etiquette: 'débit en mise en route',
    verbe: ['porte', 'portent'],
    // La faute inverse. Décision de Charles, 27 août : le débit se fait
    // sans mise en train. Une ligne de scie rangée là facture un montage
    // sur un poste qui n'en a pas, et le total monte.
    texte: 'un débit en « mise en route ». Le débit se fait sans mise en train : '
      + 'sa place est en « production ».',
  },
  {
    cle: 'debits_sur_tour_a_barre',
    etiquette: 'débit sur un tour à barre',
    verbe: ['porte', 'portent'],
    // Mesuré le 7 septembre sur la « Visserie hexagonale M6 » : l'agent
    // propose 3 min/pièce de sciage là où Mertens compte zéro, parce que
    // le CTX 400 tronçonne dans son cycle. Sur 200 pièces, dix heures.
    texte: 'un débit sur une machine qui s’alimente en barre. Elle tronçonne '
      + 'dans son cycle : cette ligne fait probablement double emploi.',
  },
];

export function AlertesGamme({ derive = {}, gamme = [] }) {
  const posees = ALERTES
    .map((a) => ({ ...a, ops: derive[a.cle] ?? [] }))
    .filter((a) => a.ops.length);
  const effets = effetsAppliques(gamme);
  if (!posees.length && !effets.length) return null;

  return (
    <>
      {effets.map((e) => (
        <p key={`regle-${e.regle_id}`} className="gamme__avertissement">
          <Badge tone="info">Règle appliquée</Badge>{' '}
          {`Règle n° ${e.regle_id} : ${e.texte} — ${formatNumber(e.avant, 'min')} → ${formatNumber(e.apres, 'min')}.`}
        </p>
      ))}
      {posees.map((a) => (
        <p key={a.cle} className="gamme__avertissement">
          <Badge tone="warn">{a.etiquette}</Badge>{' '}
          {sujet(a.ops, a.verbe[0], a.verbe[1])}{' '}
          {a.texte}
        </p>
      ))}
    </>
  );
}

export default AlertesGamme;
